import dagre from "./dagre-shim";
import { Node, Edge, Position } from "@xyflow/react";
import { SequenceNodeData } from "../SequenceNode";
import { InteractionType } from "../../../models/interactions/Interaction";

const DEFAULT_NODE_WIDTH = 256;
const DEFAULT_NODE_HEIGHT = 100; // Default height estimate
const GROUP_SPACING = 250; // Vertical spacing between groups

/**
 * Find all nodes reachable from a starting node via edges (BFS traversal)
 */
function findConnectedNodes(
  startNodeId: string,
  nodes: Node<SequenceNodeData>[],
  edges: Edge[],
): Set<string> {
  const visited = new Set<string>();
  const queue: string[] = [startNodeId];
  visited.add(startNodeId);

  // Build adjacency map for faster lookup
  const adjacencyMap = new Map<string, string[]>();
  edges.forEach((edge) => {
    if (!adjacencyMap.has(edge.source)) {
      adjacencyMap.set(edge.source, []);
    }
    adjacencyMap.get(edge.source)!.push(edge.target);

    // Also add reverse connections for undirected traversal
    if (!adjacencyMap.has(edge.target)) {
      adjacencyMap.set(edge.target, []);
    }
    adjacencyMap.get(edge.target)!.push(edge.source);
  });

  while (queue.length > 0) {
    const currentNodeId = queue.shift()!;
    const neighbors = adjacencyMap.get(currentNodeId) || [];

    for (const neighborId of neighbors) {
      if (!visited.has(neighborId)) {
        visited.add(neighborId);
        queue.push(neighborId);
      }
    }
  }

  return visited;
}

/**
 * Apply dagre layout to a group of nodes horizontally (LR)
 */
function layoutGroupHorizontally(
  groupNodes: Node<SequenceNodeData>[],
  groupEdges: Edge[],
  nodeDimensions: Map<string, { width: number; height: number }>,
): Node<SequenceNodeData>[] {
  const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

  // Use consistent fixed spacing values
  // nodesep: horizontal spacing between nodes (left-right in LR layout)
  // ranksep: vertical spacing between ranks (top-bottom in LR layout)
  dagreGraph.setGraph({
    rankdir: "LR",
    nodesep: 60, // Reduced horizontal spacing between nodes
    ranksep: 100, // Reduced vertical spacing between ranks
  });

  // Separate edges: primary flow (regular + primary-output) vs secondary-output and If node subgroups
  const primaryEdges = groupEdges.filter(
    (edge) =>
      edge.sourceHandle !== "secondary-output" &&
      edge.sourceHandle !== "onTrue" &&
      edge.sourceHandle !== "onFalse",
  );
  const secondaryOutputEdges = groupEdges.filter(
    (edge) => edge.sourceHandle === "secondary-output",
  );

  // Add nodes to dagre graph with actual dimensions
  groupNodes.forEach((node) => {
    const dimensions = nodeDimensions.get(node.id) || {
      width: DEFAULT_NODE_WIDTH,
      height: DEFAULT_NODE_HEIGHT,
    };
    dagreGraph.setNode(node.id, {
      width: dimensions.width,
      height: dimensions.height,
    });
  });

  // Add only primary edges to dagre graph (secondary-output and If node subgroups will be handled separately)
  primaryEdges.forEach((edge) => {
    if (
      groupNodes.some((n) => n.id === edge.source) &&
      groupNodes.some((n) => n.id === edge.target)
    ) {
      dagreGraph.setEdge(edge.source, edge.target);
    }
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Get positions from dagre and apply post-processing for siblings
  const layoutedNodes = groupNodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const dimensions = nodeDimensions.get(node.id) || {
      width: DEFAULT_NODE_WIDTH,
      height: DEFAULT_NODE_HEIGHT,
    };
    return {
      ...node,
      targetPosition: Position.Left,
      sourcePosition: Position.Right,
      position: {
        x: nodeWithPosition.x - dimensions.width / 2,
        y: nodeWithPosition.y - dimensions.height / 2,
      },
      _dagreY: nodeWithPosition.y, // Store original Y for sibling detection
      _dimensions: dimensions,
    };
  });

  // Post-process: Add extra vertical spacing for nodes connected to the same source
  // Only adjust if they're at approximately the same rank (similar X position)
  const EXTRA_VERTICAL_SPACING = 40; // Reduced extra spacing for siblings
  const RANK_TOLERANCE = 100; // Nodes within 100px X distance are considered same rank

  const nodesBySource = new Map<string, typeof layoutedNodes>();
  groupEdges.forEach((edge) => {
    const targetNode = layoutedNodes.find((n) => n.id === edge.target);
    if (targetNode) {
      if (!nodesBySource.has(edge.source)) {
        nodesBySource.set(edge.source, []);
      }
      nodesBySource.get(edge.source)!.push(targetNode);
    }
  });

  // Adjust positions for siblings at the same rank
  nodesBySource.forEach((siblingNodes, sourceId) => {
    if (siblingNodes.length > 1) {
      // Group siblings by their rank (X position, rounded to nearest tolerance)
      const siblingsByRank = new Map<number, typeof siblingNodes>();
      siblingNodes.forEach((node) => {
        const rank =
          Math.round(node.position.x / RANK_TOLERANCE) * RANK_TOLERANCE;
        if (!siblingsByRank.has(rank)) {
          siblingsByRank.set(rank, []);
        }
        siblingsByRank.get(rank)!.push(node);
      });

      // Adjust each rank group separately
      siblingsByRank.forEach((siblingsAtRank) => {
        if (siblingsAtRank.length > 1) {
          // Sort by Y position
          const sorted = [...siblingsAtRank].sort(
            (a, b) => a.position.y - b.position.y,
          );

          // Calculate minimum spacing needed
          let currentY = sorted[0].position.y;
          sorted.forEach((node, index) => {
            if (index > 0) {
              const prevNode = sorted[index - 1];
              const minSpacing =
                prevNode._dimensions.height + EXTRA_VERTICAL_SPACING;
              const currentSpacing =
                node.position.y -
                (prevNode.position.y + prevNode._dimensions.height);

              // Only adjust if current spacing is less than desired spacing
              if (currentSpacing < EXTRA_VERTICAL_SPACING) {
                currentY =
                  prevNode.position.y +
                  prevNode._dimensions.height +
                  EXTRA_VERTICAL_SPACING;
              } else {
                currentY = node.position.y; // Keep dagre's spacing if it's already good
              }
            }
            node.position.y = currentY;
          });
        }
      });
    }
  });

  // Return nodes without internal properties (secondary groups will be handled at top level)
  return layoutedNodes.map(({ _dagreY, _dimensions, ...node }) => node);
}

/**
 * Calculate bounding box for a group of nodes
 */
function getGroupBounds(
  nodes: Node<SequenceNodeData>[],
  nodeDimensions: Map<string, { width: number; height: number }>,
): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
} {
  if (nodes.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  nodes.forEach((node) => {
    const dimensions = nodeDimensions.get(node.id) || {
      width: DEFAULT_NODE_WIDTH,
      height: DEFAULT_NODE_HEIGHT,
    };
    minX = Math.min(minX, node.position.x);
    minY = Math.min(minY, node.position.y);
    maxX = Math.max(maxX, node.position.x + dimensions.width);
    maxY = Math.max(maxY, node.position.y + dimensions.height);
  });

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/**
 * Apply hierarchical layout: groups laid out horizontally, arranged vertically
 */
export function applyDagreLayout(
  nodes: Node<SequenceNodeData>[],
  edges: Edge[],
  nodeDimensions: Map<string, { width: number; height: number }>,
): Node<SequenceNodeData>[] {
  // Filter out temporary nodes and edges
  const realNodes = nodes.filter((node) => node.id !== "temp-target");
  const realEdges = edges.filter(
    (edge) => !edge.id?.startsWith("temp-connection-"),
  );

  if (realNodes.length === 0) {
    return nodes;
  }

  // Identify group starter nodes (Sequence and SubSequence nodes)
  const groupStarters = realNodes.filter(
    (node) =>
      node.data.interaction.type === InteractionType.Sequence ||
      node.data.interaction.type === InteractionType.SubSequence ||
      node.data.interaction.type === InteractionType.Trigger,
  );

  // Layout each group horizontally (or single group when no starters, e.g. selection with only ParallelSequence + children)
  const layoutedGroups: Array<{
    nodes: Node<SequenceNodeData>[];
    bounds: ReturnType<typeof getGroupBounds>;
  }> = [];

  if (groupStarters.length === 0) {
    // No group starters (e.g. formatting selection without Sequence/SubSequence)
    // Layout all nodes as a single group; secondary-output handling will still run below
    const layoutedNodes = layoutGroupHorizontally(
      realNodes,
      realEdges,
      nodeDimensions,
    );
    layoutedGroups.push({
      nodes: layoutedNodes,
      bounds: getGroupBounds(layoutedNodes, nodeDimensions),
    });
  } else {
    // Find all nodes belonging to each group
    const groups: Array<{
      starter: Node<SequenceNodeData>;
      nodeIds: Set<string>;
    }> = [];

    groupStarters.forEach((starter) => {
      const connectedNodeIds = findConnectedNodes(
        starter.id,
        realNodes,
        realEdges,
      );
      groups.push({ starter, nodeIds: connectedNodeIds });
    });

    // Handle nodes that don't belong to any group (orphaned nodes)
    const groupedNodeIds = new Set<string>();
    groups.forEach((group) => {
      group.nodeIds.forEach((id) => groupedNodeIds.add(id));
    });

    const orphanedNodes = realNodes.filter(
      (node) => !groupedNodeIds.has(node.id),
    );

    groups.forEach((group) => {
      const groupNodes = realNodes.filter((node) => group.nodeIds.has(node.id));
      const groupEdges = realEdges.filter(
        (edge) =>
          group.nodeIds.has(edge.source) && group.nodeIds.has(edge.target),
      );

      const layoutedNodes = layoutGroupHorizontally(
        groupNodes,
        groupEdges,
        nodeDimensions,
      );
      const bounds = getGroupBounds(layoutedNodes, nodeDimensions);
      layoutedGroups.push({ nodes: layoutedNodes, bounds });
    });

    // Handle orphaned nodes as a separate group
    if (orphanedNodes.length > 0) {
      const orphanedEdges = realEdges.filter(
        (edge) =>
          orphanedNodes.some((n) => n.id === edge.source) &&
          orphanedNodes.some((n) => n.id === edge.target),
      );
      const layoutedOrphaned = layoutGroupHorizontally(
        orphanedNodes,
        orphanedEdges,
        nodeDimensions,
      );
      const bounds = getGroupBounds(layoutedOrphaned, nodeDimensions);
      layoutedGroups.push({ nodes: layoutedOrphaned, bounds });
    }
  }

  // Sort groups: Main Sequence first, then SubSequences, then orphaned
  layoutedGroups.sort((a, b) => {
    const aStarter = a.nodes.find(
      (n) =>
        n.data.interaction.type === InteractionType.Sequence ||
        n.data.interaction.type === InteractionType.SubSequence ||
        n.data.interaction.type === InteractionType.Trigger,
    );
    const bStarter = b.nodes.find(
      (n) =>
        n.data.interaction.type === InteractionType.Sequence ||
        n.data.interaction.type === InteractionType.SubSequence ||
        n.data.interaction.type === InteractionType.Trigger,
    );

    if (!aStarter && !bStarter) return 0;
    if (!aStarter) return 1; // Orphaned goes last
    if (!bStarter) return -1; // Orphaned goes last

    // Main Sequence goes first
    if (
      aStarter.data.interaction.type === InteractionType.Sequence &&
      bStarter.data.interaction.type !== InteractionType.Sequence
    ) {
      return -1;
    }
    if (
      bStarter.data.interaction.type === InteractionType.Sequence &&
      aStarter.data.interaction.type !== InteractionType.Sequence
    ) {
      return 1;
    }

    return 0;
  });

  // Arrange groups vertically
  let currentY = 0;
  const finalNodes: Node<SequenceNodeData>[] = [];

  layoutedGroups.forEach((group) => {
    const offsetY = currentY - group.bounds.minY;
    const offsetX = -group.bounds.minX; // Center groups horizontally (optional)

    group.nodes.forEach((node) => {
      finalNodes.push({
        ...node,
        position: {
          x: node.position.x + offsetX,
          y: node.position.y + offsetY,
        },
      });
    });

    currentY = group.bounds.maxY + GROUP_SPACING;
  });

  // Preserve nodes that weren't in any group (shouldn't happen, but safety check)
  const finalNodeIds = new Set(finalNodes.map((n) => n.id));
  realNodes.forEach((node) => {
    if (!finalNodeIds.has(node.id)) {
      finalNodes.push(node);
    }
  });

  // Handle secondary-output edges: layout them horizontally below all main sequence nodes
  const secondaryOutputEdges = realEdges.filter(
    (edge) => edge.sourceHandle === "secondary-output",
  );

  if (secondaryOutputEdges.length > 0) {
    // Find the maximum Y position of all nodes (bottom of main sequence)
    let maxY = -Infinity;
    finalNodes.forEach((node) => {
      const dimensions = nodeDimensions.get(node.id) || {
        width: DEFAULT_NODE_WIDTH,
        height: DEFAULT_NODE_HEIGHT,
      };
      const nodeBottomY = node.position.y + dimensions.height;
      maxY = Math.max(maxY, nodeBottomY);
    });

    // Create a separate group for each secondary-output edge
    // Each edge represents a distinct secondary group from the same source
    const primaryEdges = realEdges.filter(
      (edge) =>
        edge.sourceHandle !== "secondary-output" &&
        edge.sourceHandle !== "onTrue" &&
        edge.sourceHandle !== "onFalse",
    );

    interface SecondaryGroup {
      sourceId: string;
      sourceNode: Node<SequenceNodeData> | undefined;
      nodeIds: Set<string>;
      startTargetId: string; // The initial target from the secondary-output edge
    }

    const secondaryGroups: SecondaryGroup[] = [];

    // Create a separate group for each secondary-output edge
    secondaryOutputEdges.forEach((edge) => {
      const nodeIds = new Set<string>([edge.target]);

      // Find all nodes reachable from this secondary-output target (transitive closure)
      const visited = new Set<string>();
      const queue = [edge.target];

      while (queue.length > 0) {
        const currentNodeId = queue.shift()!;
        if (visited.has(currentNodeId)) continue;
        visited.add(currentNodeId);

        // Find all nodes connected from this node (via primary edges, not secondary)
        primaryEdges.forEach((primaryEdge) => {
          if (
            primaryEdge.source === currentNodeId &&
            !visited.has(primaryEdge.target)
          ) {
            nodeIds.add(primaryEdge.target);
            queue.push(primaryEdge.target);
          }
        });
      }

      const sourceNode = finalNodes.find((n) => n.id === edge.source);
      secondaryGroups.push({
        sourceId: edge.source,
        sourceNode,
        nodeIds,
        startTargetId: edge.target,
      });
    });

    // Group secondary groups by their source node
    const groupsBySource = new Map<string, SecondaryGroup[]>();
    secondaryGroups.forEach((group) => {
      if (!groupsBySource.has(group.sourceId)) {
        groupsBySource.set(group.sourceId, []);
      }
      groupsBySource.get(group.sourceId)!.push(group);
    });

    // Sort source nodes by X position (left to right)
    const sortedSources = Array.from(groupsBySource.entries())
      .map(([sourceId, groups]) => {
        const sourceNode = finalNodes.find((n) => n.id === sourceId);
        return { sourceId, groups, sourceNode };
      })
      .filter((entry) => entry.sourceNode)
      .sort((a, b) => {
        const aX = a.sourceNode!.position.x;
        const bX = b.sourceNode!.position.x;
        return aX - bX;
      });

    // Position secondary groups vertically below their source nodes
    // Groups from the same source are stacked vertically
    const SECONDARY_GROUP_SPACING = 80; // Vertical spacing between groups from same source
    const SECONDARY_PARENT_SPACING = 250; // Vertical spacing between parent and first child group
    const SECONDARY_HORIZONTAL_SPACING = 60; // Horizontal spacing within secondary group

    sortedSources.forEach(({ sourceId, groups, sourceNode }) => {
      if (!sourceNode || groups.length === 0) return;

      // Get source node dimensions
      const sourceDimensions = nodeDimensions.get(sourceId) || {
        width: DEFAULT_NODE_WIDTH,
        height: DEFAULT_NODE_HEIGHT,
      };
      const sourceBottomY = sourceNode.position.y + sourceDimensions.height;

      // Start positioning groups with spacing below this source node
      let currentSecondaryY = sourceBottomY + SECONDARY_PARENT_SPACING;

      // Position groups from this source vertically stacked
      // All groups align with source's X position
      const sourceX = sourceNode.position.x;

      groups.forEach((group) => {
        const { nodeIds } = group;
        const secondaryNodes = finalNodes.filter((n) => nodeIds.has(n.id));
        if (secondaryNodes.length === 0) return;

        // Get all edges for this secondary group (both secondary-output and internal connections)
        const secondaryGroupEdges = realEdges.filter(
          (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target),
        );

        // Create a mini dagre graph for the secondary group
        const secondaryGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(
          () => ({}),
        );
        secondaryGraph.setGraph({
          rankdir: "LR",
          nodesep: SECONDARY_HORIZONTAL_SPACING,
          ranksep: 100,
        });

        // Add secondary nodes to the graph
        secondaryNodes.forEach((node) => {
          const dimensions = nodeDimensions.get(node.id) || {
            width: DEFAULT_NODE_WIDTH,
            height: DEFAULT_NODE_HEIGHT,
          };
          secondaryGraph.setNode(node.id, {
            width: dimensions.width,
            height: dimensions.height,
          });
        });

        // Add edges between secondary nodes
        secondaryGroupEdges.forEach((edge) => {
          if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
            secondaryGraph.setEdge(edge.source, edge.target);
          }
        });

        // Layout the secondary group
        dagre.layout(secondaryGraph);

        // Find the leftmost X position and topmost Y position from dagre layout
        let minX = Infinity;
        let minY = Infinity;
        secondaryNodes.forEach((node) => {
          const nodeWithPosition = secondaryGraph.node(node.id);
          const dimensions = nodeDimensions.get(node.id) || {
            width: DEFAULT_NODE_WIDTH,
            height: DEFAULT_NODE_HEIGHT,
          };
          const nodeX = nodeWithPosition.x - dimensions.width / 2;
          const nodeY = nodeWithPosition.y - dimensions.height / 2;
          minX = Math.min(minX, nodeX);
          minY = Math.min(minY, nodeY);
        });

        // Position all secondary nodes aligned with source's X position
        const xOffset = sourceX - minX;
        // Position vertically preserving dagre's relative Y positions
        const yOffset = currentSecondaryY - minY;

        secondaryNodes.forEach((node) => {
          const nodeWithPosition = secondaryGraph.node(node.id);
          const dimensions = nodeDimensions.get(node.id) || {
            width: DEFAULT_NODE_WIDTH,
            height: DEFAULT_NODE_HEIGHT,
          };
          
          // Position horizontally using dagre's layout, aligned with source
          node.position.x = nodeWithPosition.x - dimensions.width / 2 + xOffset;
          // Position vertically preserving dagre's relative Y positions to prevent overlap
          node.position.y = nodeWithPosition.y - dimensions.height / 2 + yOffset;
        });

        // Calculate the height of this group for vertical stacking
        let groupMinY = Infinity;
        let groupMaxY = -Infinity;
        secondaryNodes.forEach((node) => {
          const dimensions = nodeDimensions.get(node.id) || {
            width: DEFAULT_NODE_WIDTH,
            height: DEFAULT_NODE_HEIGHT,
          };
          groupMinY = Math.min(groupMinY, node.position.y);
          groupMaxY = Math.max(groupMaxY, node.position.y + dimensions.height);
        });

        // Update Y position for next group from same source (stack vertically)
        currentSecondaryY = groupMaxY + SECONDARY_GROUP_SPACING;
      });
    });
  }

  // Handle If node onTrue/onFalse edges: layout them horizontally below their source If nodes
  const ifSubgroupEdges = realEdges.filter(
    (edge) => edge.sourceHandle === "onTrue" || edge.sourceHandle === "onFalse",
  );

  if (ifSubgroupEdges.length > 0) {
    // Find the maximum Y position of all nodes (including secondary groups)
    let maxY = -Infinity;
    finalNodes.forEach((node) => {
      const dimensions = nodeDimensions.get(node.id) || {
        width: DEFAULT_NODE_WIDTH,
        height: DEFAULT_NODE_HEIGHT,
      };
      const nodeBottomY = node.position.y + dimensions.height;
      maxY = Math.max(maxY, nodeBottomY);
    });

    // Create a separate group for each onTrue/onFalse edge
    const primaryEdgesForIf = realEdges.filter(
      (edge) =>
        edge.sourceHandle !== "secondary-output" &&
        edge.sourceHandle !== "onTrue" &&
        edge.sourceHandle !== "onFalse",
    );

    interface IfSubgroup {
      sourceId: string;
      sourceNode: Node<SequenceNodeData> | undefined;
      nodeIds: Set<string>;
      startTargetId: string;
      handleType: "onTrue" | "onFalse";
    }

    const ifSubgroups: IfSubgroup[] = [];

    // Create a separate group for each onTrue/onFalse edge
    ifSubgroupEdges.forEach((edge) => {
      const nodeIds = new Set<string>([edge.target]);

      // Find all nodes reachable from this subgroup target (transitive closure)
      const visited = new Set<string>();
      const queue = [edge.target];

      while (queue.length > 0) {
        const currentNodeId = queue.shift()!;
        if (visited.has(currentNodeId)) continue;
        visited.add(currentNodeId);

        // Find all nodes connected from this node (via primary edges, not subgroup edges)
        primaryEdgesForIf.forEach((primaryEdge) => {
          if (
            primaryEdge.source === currentNodeId &&
            !visited.has(primaryEdge.target)
          ) {
            nodeIds.add(primaryEdge.target);
            queue.push(primaryEdge.target);
          }
        });
      }

      const sourceNode = finalNodes.find((n) => n.id === edge.source);
      ifSubgroups.push({
        sourceId: edge.source,
        sourceNode,
        nodeIds,
        startTargetId: edge.target,
        handleType: edge.sourceHandle as "onTrue" | "onFalse",
      });
    });

    // Group subgroups by their source node and handle type
    const groupsBySourceAndHandle = new Map<
      string,
      Map<"onTrue" | "onFalse", IfSubgroup[]>
    >();
    ifSubgroups.forEach((group) => {
      if (!groupsBySourceAndHandle.has(group.sourceId)) {
        groupsBySourceAndHandle.set(group.sourceId, new Map());
      }
      const handleMap = groupsBySourceAndHandle.get(group.sourceId)!;
      if (!handleMap.has(group.handleType)) {
        handleMap.set(group.handleType, []);
      }
      handleMap.get(group.handleType)!.push(group);
    });

    // Sort source nodes by X position (left to right)
    const sortedSources = Array.from(groupsBySourceAndHandle.entries())
      .map(([sourceId, handleMap]) => {
        const sourceNode = finalNodes.find((n) => n.id === sourceId);
        return { sourceId, handleMap, sourceNode };
      })
      .filter((entry) => entry.sourceNode)
      .sort((a, b) => {
        const aX = a.sourceNode!.position.x;
        const bX = b.sourceNode!.position.x;
        return aX - bX;
      });

    // Position If subgroups vertically below their source nodes
    // Groups from the same source are stacked vertically
    const IF_SUBGROUP_SPACING = 80; // Vertical spacing between groups from same source
    const IF_PARENT_SPACING = 250; // Vertical spacing between parent and first child group
    const IF_HORIZONTAL_SPACING = 60; // Horizontal spacing within subgroup
    const IF_HORIZONTAL_OFFSET = 100; // Horizontal offset for onTrue (right) and onFalse (left)

    sortedSources.forEach(({ sourceId, handleMap, sourceNode }) => {
      if (!sourceNode) return;

      // Get source node dimensions
      const sourceDimensions = nodeDimensions.get(sourceId) || {
        width: DEFAULT_NODE_WIDTH,
        height: DEFAULT_NODE_HEIGHT,
      };
      const sourceBottomY = sourceNode.position.y + sourceDimensions.height;

      // Process onTrue groups first, then onFalse groups
      // Start positioning groups with spacing below this source node
      let currentIfY = sourceBottomY + IF_PARENT_SPACING;

      // Process onTrue groups first
      const onTrueGroups = handleMap.get("onTrue") || [];
      if (onTrueGroups.length > 0) {
        const sourceX = sourceNode.position.x;
        const horizontalOffset = IF_HORIZONTAL_OFFSET; // Right side

        onTrueGroups.forEach((group) => {
          const { nodeIds } = group;
          const subgroupNodes = finalNodes.filter((n) => nodeIds.has(n.id));
          if (subgroupNodes.length === 0) return;

          // Get all edges for this subgroup (both onTrue/onFalse and internal connections)
          const subgroupEdges = realEdges.filter(
            (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target),
          );

          // Create a mini dagre graph for the subgroup
          const subgroupGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(
            () => ({}),
          );
          subgroupGraph.setGraph({
            rankdir: "LR",
            nodesep: IF_HORIZONTAL_SPACING,
            ranksep: 100,
          });

          // Add subgroup nodes to the graph
          subgroupNodes.forEach((node) => {
            const dimensions = nodeDimensions.get(node.id) || {
              width: DEFAULT_NODE_WIDTH,
              height: DEFAULT_NODE_HEIGHT,
            };
            subgroupGraph.setNode(node.id, {
              width: dimensions.width,
              height: dimensions.height,
            });
          });

          // Add edges between subgroup nodes
          subgroupEdges.forEach((edge) => {
            if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
              subgroupGraph.setEdge(edge.source, edge.target);
            }
          });

          // Layout the subgroup
          dagre.layout(subgroupGraph);

          // Find the leftmost X position and topmost Y position from dagre layout
          let minX = Infinity;
          let minY = Infinity;
          subgroupNodes.forEach((node) => {
            const nodeWithPosition = subgroupGraph.node(node.id);
            const dimensions = nodeDimensions.get(node.id) || {
              width: DEFAULT_NODE_WIDTH,
              height: DEFAULT_NODE_HEIGHT,
            };
            const nodeX = nodeWithPosition.x - dimensions.width / 2;
            const nodeY = nodeWithPosition.y - dimensions.height / 2;
            minX = Math.min(minX, nodeX);
            minY = Math.min(minY, nodeY);
          });

          // Position all subgroup nodes aligned with source's X position + offset
          const xOffset = sourceX + horizontalOffset - minX;
          // Position vertically preserving dagre's relative Y positions
          const yOffset = currentIfY - minY;

          subgroupNodes.forEach((node) => {
            const nodeWithPosition = subgroupGraph.node(node.id);
            const dimensions = nodeDimensions.get(node.id) || {
              width: DEFAULT_NODE_WIDTH,
              height: DEFAULT_NODE_HEIGHT,
            };

            // Position horizontally using dagre's layout, aligned with source + offset
            node.position.x = nodeWithPosition.x - dimensions.width / 2 + xOffset;
            // Position vertically preserving dagre's relative Y positions to prevent overlap
            node.position.y = nodeWithPosition.y - dimensions.height / 2 + yOffset;
          });

          // Calculate the height of this group for vertical stacking
          let groupMinY = Infinity;
          let groupMaxY = -Infinity;
          subgroupNodes.forEach((node) => {
            const dimensions = nodeDimensions.get(node.id) || {
              width: DEFAULT_NODE_WIDTH,
              height: DEFAULT_NODE_HEIGHT,
            };
            groupMinY = Math.min(groupMinY, node.position.y);
            groupMaxY = Math.max(groupMaxY, node.position.y + dimensions.height);
          });

          // Update Y position for next group from same source (stack vertically)
          currentIfY = groupMaxY + IF_SUBGROUP_SPACING;
        });
      }

      // Process onFalse groups after onTrue groups
      const onFalseGroups = handleMap.get("onFalse") || [];
      if (onFalseGroups.length > 0) {
        const sourceX = sourceNode.position.x;
        const horizontalOffset = -IF_HORIZONTAL_OFFSET; // Left side

        onFalseGroups.forEach((group) => {
          const { nodeIds } = group;
          const subgroupNodes = finalNodes.filter((n) => nodeIds.has(n.id));
          if (subgroupNodes.length === 0) return;

          // Get all edges for this subgroup (both onTrue/onFalse and internal connections)
          const subgroupEdges = realEdges.filter(
            (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target),
          );

          // Create a mini dagre graph for the subgroup
          const subgroupGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(
            () => ({}),
          );
          subgroupGraph.setGraph({
            rankdir: "LR",
            nodesep: IF_HORIZONTAL_SPACING,
            ranksep: 100,
          });

          // Add subgroup nodes to the graph
          subgroupNodes.forEach((node) => {
            const dimensions = nodeDimensions.get(node.id) || {
              width: DEFAULT_NODE_WIDTH,
              height: DEFAULT_NODE_HEIGHT,
            };
            subgroupGraph.setNode(node.id, {
              width: dimensions.width,
              height: dimensions.height,
            });
          });

          // Add edges between subgroup nodes
          subgroupEdges.forEach((edge) => {
            if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
              subgroupGraph.setEdge(edge.source, edge.target);
            }
          });

          // Layout the subgroup
          dagre.layout(subgroupGraph);

          // Find the leftmost X position and topmost Y position from dagre layout
          let minX = Infinity;
          let minY = Infinity;
          subgroupNodes.forEach((node) => {
            const nodeWithPosition = subgroupGraph.node(node.id);
            const dimensions = nodeDimensions.get(node.id) || {
              width: DEFAULT_NODE_WIDTH,
              height: DEFAULT_NODE_HEIGHT,
            };
            const nodeX = nodeWithPosition.x - dimensions.width / 2;
            const nodeY = nodeWithPosition.y - dimensions.height / 2;
            minX = Math.min(minX, nodeX);
            minY = Math.min(minY, nodeY);
          });

          // Position all subgroup nodes aligned with source's X position + offset
          const xOffset = sourceX + horizontalOffset - minX;
          // Position vertically preserving dagre's relative Y positions
          const yOffset = currentIfY - minY;

          subgroupNodes.forEach((node) => {
            const nodeWithPosition = subgroupGraph.node(node.id);
            const dimensions = nodeDimensions.get(node.id) || {
              width: DEFAULT_NODE_WIDTH,
              height: DEFAULT_NODE_HEIGHT,
            };

            // Position horizontally using dagre's layout, aligned with source + offset
            node.position.x = nodeWithPosition.x - dimensions.width / 2 + xOffset;
            // Position vertically preserving dagre's relative Y positions to prevent overlap
            node.position.y = nodeWithPosition.y - dimensions.height / 2 + yOffset;
          });

          // Calculate the height of this group for vertical stacking
          let groupMinY = Infinity;
          let groupMaxY = -Infinity;
          subgroupNodes.forEach((node) => {
            const dimensions = nodeDimensions.get(node.id) || {
              width: DEFAULT_NODE_WIDTH,
              height: DEFAULT_NODE_HEIGHT,
            };
            groupMinY = Math.min(groupMinY, node.position.y);
            groupMaxY = Math.max(groupMaxY, node.position.y + dimensions.height);
          });

          // Update Y position for next group from same source (stack vertically)
          currentIfY = groupMaxY + IF_SUBGROUP_SPACING;
        });
      }
    });
  }

  return finalNodes;
}
